import React from 'react';
import { Spinner } from 'react-bootstrap';
import { MapPin } from 'lucide-react';
import { Building } from '../../../types/protocols';
import { CheckLocationRef, useCheckLocation } from '../../../hooks/inspections/useCheckLocation';
import { ComponentList, MarkerImage, SectionTitle } from '../../WorkOrders/components/shared';
import './CheckLocationSection.css';

export interface CheckLocationSectionProps {
    buildingKey: string | null | undefined;
    check: CheckLocationRef | null | undefined;
    /** The check's building when the page already has it (with its model). */
    building?: Building | null;
}

/**
 * Where a check is, as a work order shows its components: the check's component
 * (name, plats, link to the component page, "Hitta komponenterna"), or "Plats"
 * with the marker of a check linked to a place. Renders nothing for
 * building-wide checks and Skickbedömning.
 */
const CheckLocationSection: React.FC<CheckLocationSectionProps> = ({ buildingKey, check, building }) => {
    const { componentItems, componentMissing, locationImage, loading } = useCheckLocation(buildingKey, check, building);

    if (loading) {
        return (
            <div className="d-flex align-items-center gap-2 text-muted mb-3">
                <Spinner animation="border" size="sm" />
                <span>Hämtar plats...</span>
            </div>
        );
    }

    if (componentItems.length > 0) {
        return (
            <div className="check-location">
                <ComponentList components={componentItems} />
            </div>
        );
    }

    if (componentMissing) {
        return <p className="text-muted small mb-3">Komponenten finns inte längre i byggnaden.</p>;
    }

    if (locationImage) {
        return (
            <div className="check-location">
                <section className="wo-card">
                    <SectionTitle icon={MapPin} title="Plats" />
                    <div className="wo-component-locations__images">
                        <MarkerImage image={locationImage} />
                    </div>
                </section>
            </div>
        );
    }

    return null;
};

export default CheckLocationSection;
